import {
  IBayLevelData,
  ILashingBridge,
  ILashingBridgeHeight,
  ILashingBridgeInfo,
  ISizeSummary,
} from "open-vessel-definition";

import { IRowPattern } from "../types/IPositionPatterns";
import { bayLevelHasCenterLineRow } from "../bayLevelHasCenterLineRow";
import { createRowsFromConfig } from "../createRowsFromConfig";

export type TLashingBridgeSide = "fore" | "aft";

/** Sides in the order they are shown and processed (AFT first, as in the Bulkheads editor) */
export const LASHING_BRIDGE_SIDES: readonly TLashingBridgeSide[] = ["aft", "fore"];

export type TLashingBridgePerRow = NonNullable<ILashingBridge["perRow"]>;

type TUnknownRecord = Record<string, unknown>;

const isRecord = (v: unknown): v is TUnknownRecord =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const isFiniteNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

const ROW_KEY_REGEX = /^\d{2}$/;

/** Keys used by the OVD 1.2.0 flat shape (`fore: 0|1, foreTiers, foreLcg`) */
function legacyKeys(side: TLashingBridgeSide): [string, string] {
  return [`${side}Tiers`, `${side}Lcg`];
}

/** Whether a side of `lashingBridges` is written with the OVD 1.2.0 flat shape */
export function isLegacyLashingBridgeSide(lb: TUnknownRecord, side: TLashingBridgeSide): boolean {
  if (typeof lb[side] === "number") return true;
  return !isRecord(lb[side]) && legacyKeys(side).some((k) => lb[k] !== undefined);
}

/**
 * Converts one side written with the OVD 1.2.0 flat shape to the 1.3.0 shape.
 * @returns `undefined` when the side is not legacy, `null` when it is legacy and "off",
 * the converted bridge, or `"unconvertible"` when the legacy data is inconsistent.
 */
export function convertLegacyLashingBridgeSide(
  lb: TUnknownRecord,
  side: TLashingBridgeSide,
): ILashingBridge | null | undefined | "unconvertible" {
  if (!isLegacyLashingBridgeSide(lb, side)) return undefined;

  const [tiersKey, lcgKey] = legacyKeys(side);
  const on = lb[side];
  const tiers = lb[tiersKey];
  const lcg = lb[lcgKey];

  if (on === 0 || on === undefined) {
    // Off: no tiers or lcg allowed. A missing flag with only legacy keys can't be trusted either
    return on === 0 && tiers === undefined && lcg === undefined ? null : "unconvertible";
  }

  if (on !== 1 || !isFiniteNumber(tiers) || tiers < 0) return "unconvertible";
  if (lcg !== undefined && !isFiniteNumber(lcg)) return "unconvertible";

  const bridge: ILashingBridge = { tiers };
  if (lcg !== undefined) bridge.lcg = lcg;
  return bridge;
}

function normalizeHeight(raw: unknown): ILashingBridgeHeight | undefined {
  if (!isRecord(raw)) return undefined;
  if (!isFiniteNumber(raw["tiers"]) || raw["tiers"] < 0) return undefined;

  const h: ILashingBridgeHeight = { tiers: raw["tiers"] };
  if (isFiniteNumber(raw["height"]) && raw["height"] >= 0) h.height = raw["height"];
  return h;
}

function normalizeBridge(raw: unknown): ILashingBridge | undefined {
  const base = normalizeHeight(raw);
  if (!base || !isRecord(raw)) return undefined;

  const bridge: ILashingBridge = { ...base };
  if (isFiniteNumber(raw["lcg"])) bridge.lcg = raw["lcg"];

  if (isRecord(raw["perRow"])) {
    const perRow: TLashingBridgePerRow = {};
    Object.keys(raw["perRow"]).forEach((row) => {
      if (!ROW_KEY_REGEX.test(row)) return;
      const h = normalizeHeight((raw["perRow"] as TUnknownRecord)[row]);
      if (h) perRow[row as IRowPattern] = h;
    });
    if (Object.keys(perRow).length) bridge.perRow = perRow;
  }

  return bridge;
}

/**
 * Tolerant reader of `IBayLevelData.lashingBridges`.
 * - OVD 1.3.0 data passes through (invalid values and rows are dropped).
 * - The OVD 1.2.0 flat shape is converted.
 * - Anything else returns `undefined`.
 * It always returns a new object, so callers can mutate it.
 */
export function normalizeLashingBridgeInfo(raw: unknown): ILashingBridgeInfo | undefined {
  if (!isRecord(raw)) return undefined;

  const info: ILashingBridgeInfo = {};

  LASHING_BRIDGE_SIDES.forEach((side) => {
    const legacy = convertLegacyLashingBridgeSide(raw, side);

    if (legacy === undefined) {
      const bridge = normalizeBridge(raw[side]);
      if (bridge) info[side] = bridge;
    } else if (legacy !== null && legacy !== "unconvertible") {
      info[side] = legacy;
    }
  });

  return info.fore || info.aft ? info : undefined;
}

/**
 * Height of a lashing bridge (or a row of it) above the stack bottom, in mm.
 * `height` wins over `tiers`.
 * @param tierHeightInMm Height of one tier. OVD doesn't define it: callers usually pass the
 * average container height (8'6" by default).
 */
export function lashingBridgeHeightInMm(
  h: ILashingBridgeHeight | undefined,
  tierHeightInMm: number,
): number {
  if (!h) return 0;
  const mm = h.height ?? h.tiers * tierHeightInMm;
  return Number.isFinite(mm) ? Math.max(0, mm) : 0;
}

export interface IResolvedLashingBridgeRow extends ILashingBridgeHeight {
  row: IRowPattern;
  /** True when the row has its own entry in `perRow` */
  isOverride: boolean;
}

/** Resolves the tiers/height of every given row: its `perRow` override or the bridge defaults */
export function resolveLashingBridgeRows(
  bridge: ILashingBridge,
  rows: IRowPattern[],
): IResolvedLashingBridgeRow[] {
  return rows.map((row) => {
    const override = bridge.perRow?.[row];
    const src = override ?? bridge;
    const resolved: IResolvedLashingBridgeRow = { row, tiers: src.tiers, isOverride: !!override };
    if (src.height !== undefined) resolved.height = src.height;
    return resolved;
  });
}

/**
 * Rows of a bay-level, sorted from port to starboard.
 * Uses the highest row with slots (or with `perRowInfo`), falling back to the vessel `maxRow`
 * for empty bays. Row 00 follows the centerline rule ({@link bayLevelHasCenterLineRow}).
 */
export function getBayLevelRows(
  sizeSummary: Pick<ISizeSummary, "centerLineRow" | "maxRow">,
  bl: Pick<IBayLevelData, "centerLineRow" | "perSlotInfo" | "perRowInfo"> | undefined,
): IRowPattern[] {
  const rowNumbers = [
    ...Object.keys(bl?.perSlotInfo || {}).map((k) => Number(k.substring(0, 2))),
    ...Object.keys(bl?.perRowInfo?.each || {}).map(Number),
  ].filter((n) => Number.isFinite(n));

  const maxRow = rowNumbers.length ? Math.max(...rowNumbers) : sizeSummary.maxRow;

  return createRowsFromConfig(bayLevelHasCenterLineRow(sizeSummary, bl), maxRow);
}
